import chatSocketService from "./websocket";

/**
 * Socket.io tabanlı chat servisleri
 * Firebase chat servisinin yerine geçer
 */

/**
 * Belirli bir mekan (venue) için sohbet mesajlarını dinler
 * @param {string} venueId - Mekan ID'si
 * @param {function} callback - Mesajlar her güncellendiğinde çağrılacak fonksiyon
 * @param {number} limit - Alınacak maksimum mesaj sayısı
 * @returns {function} - Socket dinleyicisini sonlandırmak için unsubscribe fonksiyonu
 */
export const listenToVenueChatMessages = (venueId, callback, limit = 50) => {
  try {
    if (!venueId)
      throw new Error("Venue ID is required for listening to messages.");
    if (typeof callback !== "function")
      throw new Error("Callback function is required.");

    // Socket bağlantısının olduğundan emin ol
    if (!chatSocketService.connected) {
      callback([], new Error("Socket bağlantısı yok"));
      return () => {};
    }

    // Mesaj dinleyicisini ekle
    const unsubscribe = chatSocketService.addMessageListener((message) => {
      // Tek mesaj formatında callback çağır
      callback([message], null);
    });

    return unsubscribe;
  } catch (error) {
    console.error("Error in listenToVenueChatMessages setup:", error);
    if (typeof callback === "function") {
      callback([], error);
    }
    return () => {};
  }
};

/**
 * Mekan sohbetine mesaj gönderir
 * @param {string} venueId - Mekan ID'si
 * @param {object} sender - Gönderen kullanıcı bilgileri { id, name, avatar }
 * @param {string} text - Mesaj metni
 * @param {string} type - Mesaj tipi: 'text', 'image', 'emoji', vb.
 * @returns {Promise<object>} - İşlem sonucu ve mesaj ID'si
 */
export const sendVenueChatMessage = async (
  venueId,
  sender,
  text,
  type = "text"
) => {
  try {
    if (!venueId || !sender || !sender.id || !text) {
      throw new Error(
        "Venue ID, sender information (id, name), and message text are required."
      );
    }

    if (!chatSocketService.connected) {
      throw new Error("Socket bağlantısı yok");
    }

    const response = await chatSocketService.sendMessage(venueId, text, type);

    return {
      success: true,
      messageId: response.messageId || Date.now().toString(),
    };
  } catch (error) {
    console.error("[sendVenueChatMessage] Error:", error);
    throw error;
  }
};

/**
 * Kullanıcının mekan sohbetindeki varlık (presence) durumunu günceller
 * @param {string} venueId - Mekan ID'si
 * @param {object} user - Kullanıcı bilgileri { id, name, avatar }
 * @param {boolean} isJoining - Katılma durumu (true: katılıyor, false: ayrılıyor)
 * @returns {Promise<object>}
 */
export const updateUserChatPresence = async (
  venueId,
  user,
  isJoining = true
) => {
  try {
    if (!venueId || !user || !user.id) {
      throw new Error(
        "Venue ID and user information (id) are required for presence update."
      );
    }

    if (!chatSocketService.connected) {
      throw new Error("Socket bağlantısı yok");
    }

    if (isJoining) {
      // Venue'ye katıl
      await chatSocketService.joinVenue(venueId);
    } else {
      // Venue'den ayrıl
      chatSocketService.leaveVenue();
    }

    return { success: true };
  } catch (error) {
    console.error("Error updating user chat presence:", error);
    throw error;
  }
};

/**
 * Kullanıcının "yazıyor..." durumunu günceller
 * @param {string} venueId - Mekan ID'si
 * @param {string} userId - Kullanıcı ID'si
 * @param {string} userName - Kullanıcı adı
 * @param {string} userAvatar - Kullanıcı avatarı
 * @param {boolean} isTyping - Yazma durumu
 * @returns {Promise<object>}
 */
export const updateUserTypingStatus = async (
  venueId,
  userId,
  userName,
  userAvatar,
  isTyping
) => {
  try {
    if (!venueId || !userId) {
      throw new Error("Venue ID and user ID are required for typing status.");
    }

    if (!chatSocketService.connected) {
      return { success: false };
    }

    chatSocketService.sendTypingStatus(venueId, isTyping);
    return { success: true };
  } catch (error) {
    console.error("Error updating typing status:", error);
    return { success: false };
  }
};

/**
 * Mekan sohbetindeki katılımcıları dinler
 * @param {string} venueId - Mekan ID'si
 * @param {function} callback - Katılımcılar güncellendiğinde çağrılacak fonksiyon
 * @returns {function} - Dinleyiciyi sonlandırmak için unsubscribe fonksiyonu
 */
export const listenToVenueChatParticipants = (venueId, callback) => {
  try {
    if (!venueId || typeof callback !== "function") {
      return () => {};
    }

    if (!chatSocketService.connected) {
      callback([]);
      return () => {};
    }

    // Kullanıcı listesi dinleyicisini ekle
    const unsubscribe = chatSocketService.addUserListener((users) => {
      callback(users || []);
    });

    return unsubscribe;
  } catch (error) {
    console.error("Error listening to venue chat participants:", error);
    if (typeof callback === "function") {
      callback([]);
    }
    return () => {};
  }
};

/**
 * Emoji reaction gönderir
 * @param {string} venueId - Mekan ID'si
 * @param {object} sender - Gönderen kullanıcı bilgileri
 * @param {string} emoji - Emoji karakteri
 * @returns {Promise<object>}
 */
export const sendEmojiReaction = async (venueId, sender, emoji) => {
  try {
    if (!venueId || !sender || !emoji) {
      throw new Error("Venue ID, sender, and emoji are required.");
    }

    if (!chatSocketService.connected) {
      throw new Error("Socket bağlantısı yok");
    }

    const response = await chatSocketService.sendEmojiReaction(venueId, emoji);
    return { success: true, reactionId: response.reactionId };
  } catch (error) {
    console.error("Error sending emoji reaction:", error);
    throw error;
  }
};

/**
 * Emoji reaction'ları dinler
 * @param {string} venueId - Mekan ID'si
 * @param {function} callback - Reaction geldiğinde çağrılacak fonksiyon
 * @returns {function} - Dinleyiciyi sonlandırmak için unsubscribe fonksiyonu
 */
export const listenToEmojiReactions = (venueId, callback) => {
  try {
    if (!venueId || typeof callback !== "function") {
      return () => {};
    }

    if (!chatSocketService.connected) {
      callback([]);
      return () => {};
    }

    let reactions = [];

    // Emoji dinleyicisini ekle
    const unsubscribe = chatSocketService.addEmojiListener((reaction) => {
      reactions.push(reaction);
      callback([...reactions], null);

      // 10 saniye sonra reaction'ı listeden kaldır
      setTimeout(() => {
        reactions = reactions.filter(r => r.id !== reaction.id);
      }, 10000);
    });

    return unsubscribe;
  } catch (error) {
    console.error("Error listening to emoji reactions:", error);
    if (typeof callback === "function") {
      callback([], error);
    }
    return () => {};
  }
};

/**
 * Vibration reaction gönderir
 * @param {string} venueId - Mekan ID'si
 * @param {object} sender - Gönderen kullanıcı bilgileri
 * @returns {Promise<object>}
 */
export const sendVibrationReaction = async (venueId, sender) => {
  try {
    if (!venueId || !sender) {
      throw new Error("Venue ID and sender are required.");
    }

    if (!chatSocketService.connected) {
      throw new Error("Socket bağlantısı yok");
    }

    const response = await chatSocketService.sendVibrationReaction(venueId);
    return { success: true, vibrationId: response.vibrationId };
  } catch (error) {
    console.error("Error sending vibration reaction:", error);
    throw error;
  }
};

/**
 * Vibration reaction'ları dinler
 * @param {string} venueId - Mekan ID'si
 * @param {function} callback - Vibration geldiğinde çağrılacak fonksiyon
 * @returns {function} - Dinleyiciyi sonlandırmak için unsubscribe fonksiyonu
 */
export const listenToVibrationReactions = (venueId, callback) => {
  try {
    if (!venueId || typeof callback !== "function") {
      return () => {};
    }

    if (!chatSocketService.connected) {
      callback([]);
      return () => {};
    }

    let vibrations = [];

    // Vibration dinleyicisini ekle
    const unsubscribe = chatSocketService.addVibrationListener((vibration) => {
      vibrations.push(vibration);
      callback([...vibrations], null);

      // 5 saniye sonra vibration'ı listeden kaldır
      setTimeout(() => {
        vibrations = vibrations.filter(v => v.id !== vibration.id);
      }, 5000);
    });

    return unsubscribe;
  } catch (error) {
    console.error("Error listening to vibration reactions:", error);
    if (typeof callback === "function") {
      callback([], error);
    }
    return () => {};
  }
}; 